import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowUp, ChevronDown, ChevronUp } from "lucide-react";
import V2BusyFrame, { V2Skeleton } from "./V2BusyFrame";
import V2PlayerAvatar from "./V2PlayerAvatar";
import { getScrollParent, smoothScrollToCenter } from "./v2CollapseScroll";
import { formatScore } from "./v2OverviewUtils";

function rankingDelta(player, metricKey) {
  const fromMetrics = player.metrics?.[metricKey]?.delta;
  if (Number.isFinite(fromMetrics)) return fromMetrics;
  const fromApi = player.deltas?.[metricKey];
  return Number.isFinite(fromApi) ? fromApi : null;
}

function rankingValue(player, metricKey) {
  const fromMetrics = player.metrics?.[metricKey]?.value;
  if (Number.isFinite(fromMetrics)) return fromMetrics;
  return Number(player[metricKey]);
}

function formatValue(value, unit, digits) {
  const formatted = formatScore(value, digits);
  if (unit === "%") return `${formatted}%`;
  return `${formatted} ${unit}`;
}

function DeltaBadge({ delta, digits, unitLabel }) {
  if (!Number.isFinite(delta) || Math.abs(delta) < 0.005) {
    return (
      <span className="inline-flex items-center rounded-full bg-[rgba(255,255,255,0.06)] px-1.5 py-[3px] text-[10px] font-bold text-[#8f8abf]">
        {formatScore(0, digits)} {unitLabel}
      </span>
    );
  }

  const up = delta > 0;
  return (
    <span
      className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-[3px] text-[10px] font-bold ${
        up
          ? "bg-[rgba(255,77,94,0.16)] text-[#ff6e7d]"
          : "bg-[rgba(52,203,255,0.12)] text-[#34cbff]"
      }`}
    >
      {up ? <ArrowUp size={10} strokeWidth={3} /> : <ArrowDown size={10} strokeWidth={3} />}
      {formatScore(Math.abs(delta), digits)} {unitLabel}
    </span>
  );
}

function KpiRow({ row, index, unit, digits, deltaDigits, deltaUnit, t, clubId }) {
  return (
    <li className="grid min-h-[44px] grid-cols-[18px_auto_1fr_auto] items-center gap-2">
      <span className="text-[12px] text-[#8b86bc]">{index + 1}</span>
      <V2PlayerAvatar
        name={row.name}
        color={row.color}
        zone={row.zone}
        size={28}
        clubId={clubId}
        playerId={row.id}
      />
      <div className="min-w-0">
        <strong className="block truncate text-[13px] text-[#f4f2ff]">{row.name}</strong>
        <small className="block text-[11px] text-[#8b86bc]">
          {t(`v2.overview.positions.${String(row.position || "mil").toLowerCase()}`)}
        </small>
      </div>
      <div className="flex items-center gap-2">
        <em className="text-[13px] font-semibold not-italic text-[#f6f3ff]">
          {formatValue(row.value, unit, digits)}
        </em>
        <DeltaBadge delta={row.delta} digits={deltaDigits} unitLabel={deltaUnit} />
      </div>
    </li>
  );
}

function RankingSkeleton() {
  return (
    <div className="grid grid-cols-2 max-[980px]:grid-cols-1">
      {[0, 1].map((column) => (
        <div
          key={column}
          className={
            column === 1
              ? "border-l border-[rgba(98,94,166,0.28)] pl-8 max-[980px]:border-l-0 max-[980px]:border-t max-[980px]:pl-0 max-[980px]:pt-4"
              : "pr-8 max-[980px]:pr-0 max-[980px]:pb-4"
          }
        >
          <V2Skeleton className="mb-3 h-2.5 w-28" />
          {[0, 1, 2].map((row) => (
            <div key={row} className="flex items-center gap-2.5 py-2">
              <V2Skeleton className="h-[26px] w-[26px] rounded-full" />
              <V2Skeleton className="h-3 w-32" />
              <V2Skeleton className="ml-auto h-3 w-16" />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export default function V2MetricRankingSection({
  players,
  metricKey,
  unit,
  digits = 1,
  deltaDigits = 1,
  clubId,
  loading = false,
}) {
  const { t } = useTranslation();
  const rootRef = useRef(null);
  const listRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const deltaUnit = t("v2.overview.metrics.delta_unit");

  const toggleRanking = () => {
    if (!expanded) {
      setExpanded(true);
      return;
    }
    const metric = rootRef.current?.closest("[data-acc]") || rootRef.current;
    const scroller = getScrollParent(rootRef.current);
    const heightDelta = listRef.current?.offsetHeight || 0;
    setExpanded(false);
    smoothScrollToCenter(scroller, metric, heightDelta);
  };

  const ranked = useMemo(() => {
    const rows = players.map((player) => ({
      ...player,
      value: rankingValue(player, metricKey),
      delta: rankingDelta(player, metricKey),
    })).filter((row) => Number.isFinite(row.value));

    const high = [...rows].sort((a, b) => b.value - a.value);
    const low = [...rows].sort((a, b) => a.value - b.value);
    const max = Math.max(...rows.map((row) => row.value), 0.0001);

    return {
      high: high.slice(0, 3),
      low: low.slice(0, 3),
      all: high,
      max,
    };
  }, [metricKey, players]);

  if (loading && ranked.all.length === 0) {
    return <RankingSkeleton />;
  }

  return (
    <V2BusyFrame loading={loading} label={t("v2.overview.fetching")}>
    <div ref={rootRef} className="scroll-mt-2">
      <div className="grid grid-cols-2 max-[980px]:grid-cols-1">
        {[
          { key: "high", title: t("v2.overview.highest_values"), rows: ranked.high },
          { key: "low", title: t("v2.overview.lowest_values"), rows: ranked.low },
        ].map((column) => (
          <div
            key={column.key}
            className={
              column.key === "low"
                ? "border-l border-[rgba(98,94,166,0.28)] pl-8 max-[980px]:border-l-0 max-[980px]:border-t max-[980px]:pl-0 max-[980px]:pt-4"
                : "pr-8 max-[980px]:pr-0 max-[980px]:pb-4"
            }
          >
            <div className="mb-2.5 flex items-end justify-between gap-2">
              <h4 className="m-0 text-[11px] font-bold uppercase tracking-[0.06em] text-[#9b96c9]">
                {column.title}
              </h4>
              <span className="text-[10px] text-[#6f6aa8]">{t("v2.overview.metrics.vs_7_days")}</span>
            </div>
            <ol className="m-0 list-none p-0">
              {column.rows.map((row, index) => (
                <KpiRow
                  key={`${column.key}-${row.id}`}
                  row={row}
                  index={index}
                  unit={unit}
                  digits={digits}
                  deltaDigits={deltaDigits}
                  deltaUnit={deltaUnit}
                  t={t}
                  clubId={clubId}
                />
              ))}
            </ol>
          </div>
        ))}
      </div>

      <div
        className={`grid transition-[grid-template-rows] duration-500 ease-in-out motion-reduce:transition-none ${
          expanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div ref={listRef} className="min-h-0 overflow-hidden" aria-hidden={!expanded} style={{ overflowAnchor: "none" }}>
          <div className="mt-5 border-t border-[rgba(98,94,166,0.35)] pt-4">
            <h4 className="mb-3 text-[11px] font-bold uppercase tracking-[0.06em] text-[#9b96c9]">
              {t("v2.overview.metrics.full_ranking", { count: ranked.all.length })}
            </h4>
            <ol className="m-0 list-none p-0">
              {ranked.all.map((row, index) => (
                <li
                  key={`all-${row.id}`}
                  className="grid min-h-[40px] grid-cols-[18px_auto_minmax(110px,168px)_minmax(64px,1fr)_auto] items-center gap-2.5 py-1 max-[720px]:grid-cols-[18px_auto_minmax(0,1fr)_auto]"
                >
                  <span className="text-[12px] text-[#8b86bc]">{index + 1}</span>
                  <V2PlayerAvatar
                    name={row.name}
                    color={row.color}
                    zone={row.zone}
                    size={26}
                    clubId={clubId}
                    playerId={row.id}
                  />
                  <strong className="truncate text-[13px] text-[#f4f2ff]">{row.name}</strong>
                  <div className="h-[6px] overflow-hidden rounded-full bg-[#1b1657] max-[720px]:hidden">
                    <span
                      className="block h-full rounded-full bg-[#34cbff]"
                      style={{ width: `${Math.max(6, (row.value / ranked.max) * 100)}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-end gap-2">
                    <em className="text-[13px] font-semibold not-italic text-[#f6f3ff]">
                      {formatValue(row.value, unit, digits)}
                    </em>
                    <DeltaBadge delta={row.delta} digits={deltaDigits} unitLabel={deltaUnit} />
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>

      {ranked.all.length > 3 && (
        <button
          type="button"
          className="mt-4 inline-flex w-full items-center justify-center gap-1.5 border-0 bg-transparent text-[12px] font-bold text-[#34cbff]"
          onClick={toggleRanking}
          aria-expanded={expanded}
        >
          {expanded
            ? t("v2.overview.metrics.collapse_ranking")
            : t("v2.overview.metrics.view_full_ranking")}
          {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
      )}
    </div>
    </V2BusyFrame>
  );
}
